import { useEffect, useId, useRef, type CSSProperties, type ReactNode, type RefObject } from 'react'

/**
 * The tenant portal's dialog shell: a header bar (title, optional subtitle,
 * close), a body, and an optional footer bar for the actions. It follows the
 * demo's `TModal` (`parkify-demo-v2-wip.jsx`), so every tenant dialog that
 * renders through it shares one layout instead of drawing its own overlay.
 *
 * Keyboard and focus follow the portal's other dialogs (`TenantWelcomeModal`,
 * Billing's statement details): focus moves in on open, Tab stays inside,
 * Escape closes, and focus goes back to whatever opened it. Dialogs can stack
 * (Invite over Manage access); only the top one answers Escape and Tab.
 */
export interface TenantModalProps {
  title: ReactNode
  sub?: ReactNode
  onClose: () => void
  children?: ReactNode
  footer?: ReactNode
  /** 620px instead of 460px, for a list such as Manage access. */
  wide?: boolean
  /** Where focus goes on open. Defaults to the close button. Pass a stable
   * `useRef` object: a new object each render re-runs the open effect. */
  initialFocus?: RefObject<HTMLElement | null>
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Open dialogs, oldest first. Only the last one handles keys. The page stops
// scrolling while any is open and gets its overflow back when the last closes,
// in whatever order they unmount.
const openDialogs: HTMLElement[] = []
let overflowBeforeFirst = ''

export function TenantModal({ title, sub, onClose, children, footer, wide, initialFocus }: TenantModalProps) {
  const titleId = useId()
  const subId = useId()
  const dialogRef = useRef<HTMLElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
    if (!openDialogs.length) {
      overflowBeforeFirst = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    openDialogs.push(dialog)
    ;(initialFocus?.current ?? closeRef.current)?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (openDialogs[openDialogs.length - 1] !== dialog) return
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const inside = dialog.contains(document.activeElement)
      if (event.shiftKey && (!inside || document.activeElement === first)) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && (!inside || document.activeElement === last)) {
        event.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const at = openDialogs.indexOf(dialog)
      if (at >= 0) openDialogs.splice(at, 1)
      if (!openDialogs.length) document.body.style.overflow = overflowBeforeFirst
      // The opener can be gone (the Remove button of a removed row); then the
      // dialog underneath, if any, takes focus so Tab stays in it.
      if (previouslyFocused?.isConnected) previouslyFocused.focus()
      else openDialogs[openDialogs.length - 1]?.focus()
    }
  }, [initialFocus])

  return (
    <div style={scrim} onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={sub ? subId : undefined}
        tabIndex={-1}
        style={{ ...panel, width: wide ? 620 : 460 }}
      >
        <div style={header}>
          <div style={{ minWidth: 0 }}>
            <h2 id={titleId} style={titleStyle}>{title}</h2>
            {sub && <p id={subId} style={subStyle}>{sub}</p>}
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" style={closeBtn}>×</button>
        </div>
        <div style={body}>{children}</div>
        {footer && <div style={footerBar}>{footer}</div>}
      </section>
    </div>
  )
}

// Values from the demo's `TModal`; colours from the portal's tokens. The scrim
// stops above a View-as banner (`--tenant-banner-h`, set by the shell).
const scrim: CSSProperties = { position: 'fixed', inset: '0 0 var(--tenant-banner-h, 0px)', zIndex: 120, background: 'rgba(26,26,26,0.44)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 20px', overflowY: 'auto' }
const panel: CSSProperties = { maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 30px 70px rgba(0,0,0,0.30)', overflow: 'hidden', outline: 'none' }
const header: CSSProperties = { padding: '18px 20px 14px', borderBottom: '1px solid var(--color-gray-200)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }
const titleStyle: CSSProperties = { fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px', margin: 0 }
const subStyle: CSSProperties = { fontSize: 12.5, fontWeight: 600, color: 'var(--color-gray-600)', lineHeight: 1.5, margin: '3px 0 0' }
const closeBtn: CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', padding: 2, fontSize: 20, lineHeight: 1, color: 'var(--color-gray-600)', fontFamily: 'inherit', flexShrink: 0 }
const body: CSSProperties = { padding: '16px 20px' }
const footerBar: CSSProperties = { padding: '13px 20px', borderTop: '1px solid var(--color-gray-200)', background: '#FCFBFD', display: 'flex', gap: 9, justifyContent: 'flex-end', flexWrap: 'wrap' }
